import { IApiError } from '@frontend/request';

import { IDailyStatistics, statisticsAPIController } from '@aqx-core/common/api/statistics';
import {
  EUserPreferenceCategory,
  EUserPreferenceType,
  userAccountSettingAPIController
} from '@aqx-core/common/api/user-account-setting';
import { mapResponseCodeToMessage } from '@aqx-core/common/request/message';

import { assetService } from './service';

class AssetViewController {
  private visibilityAC?: AbortController;
  public getDailyStatistics = async () => {
    assetService.setDailyStatisticLoading(true);
    await statisticsAPIController.getDailyStatistics({
      onSuccess: (dailyStatistics) => {
        assetService.setDailyStatistics(dailyStatistics ?? ({} as IDailyStatistics));
        assetService.setDailyStatisticLoading(false);
      },
      onError: (error) => {
        assetService.setDailyStatisticLoading(false);
        const { code, message } = error as IApiError;
        throw new Error(mapResponseCodeToMessage(code, message));
      }
    });
  };

  public getProfitStatistics = async (days: number) => {
    assetService.setProfitStatisticLoading(true);
    await statisticsAPIController.getProfitStatistics(
      {
        days
      },
      {
        onSuccess: (profitStatistics) => {
          assetService.setProfitStatistics(profitStatistics ?? []);
          assetService.setProfitStatisticLoading(false);
        },
        onError: (error) => {
          assetService.setProfitStatisticLoading(false);
          const { code, message } = error as IApiError;
          throw new Error(mapResponseCodeToMessage(code, message));
        }
      }
    );
  };

  public getAccountStatistics = async (loading = true) => {
    assetService.setDailyStatisticLoading(loading);
    await statisticsAPIController.getAccountStatistics({
      onSuccess: (accountStatistics) => {
        if (accountStatistics) {
          assetService.setAccountStatistics(accountStatistics);
        }
        assetService.setProfitStatisticLoading(false);
      },
      onError: (error) => {
        assetService.setProfitStatisticLoading(false);
        const { code, message } = error as IApiError;
        throw new Error(mapResponseCodeToMessage(code, message));
      }
    });
  };

  public getAssetVisibility = async () => {
    await userAccountSettingAPIController.getUserPreference(
      {
        type: EUserPreferenceType.SHOW_HOME_ASSET_VALUES,
        category: EUserPreferenceCategory.UI_PREFERENCE
      },
      {
        onSuccess: (preferences) => {
          if (preferences && preferences.length > 0) {
            assetService.setAssetVisibility(preferences[0].value === 'true');
          } else if (preferences && preferences.length === 0) {
            // Display assets if asset settings never updated
            assetService.setAssetVisibility(true);
          }
        },
        onError: () => {}
      }
    );
  };

  public toggleAssetVisibility = async () => {
    this.visibilityAC?.abort();
    this.visibilityAC = new AbortController();
    const currentAC = this.visibilityAC;

    const newVisibility = !assetService.context.assetVisibility;
    assetService.setAssetVisibility(newVisibility);

    await userAccountSettingAPIController.updateUserPreference(
      {
        type: EUserPreferenceType.SHOW_HOME_ASSET_VALUES,
        category: EUserPreferenceCategory.UI_PREFERENCE,
        value: String(newVisibility)
      },
      {
        onSuccess: () => {},
        onError: () => {
          if (!currentAC.signal.aborted) {
            assetService.setAssetVisibility(!newVisibility);
          }
        }
      },
      currentAC.signal
    );
  };
}

export const assetViewController = new AssetViewController();
